import { ReactComponent as ErrorInfoIcon } from 'assett/icons/error_info_icon.svg';
import { useDispatch, useSelector } from 'react-redux';
import { IMembers, IMemberSubscriptions } from 'types/Members';
import { formatDayjsForBE, formatZuluForBE, getShortDate } from 'utils/utils';

import SectionVisible from 'appContainer/SectionVisible';
import { ReactComponent as ChevronRightIcon } from 'assett/icons/chevron_right_icon_checkEligibility.svg';
import { ReactComponent as DisabledChevronRightIcon } from 'assett/icons/chevronRightIconDisabled.svg';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import { getMemberDetails } from 'feature/members/memberSlice';
import {
	addBusyIndicator,
	removeBusyIndicator,
	SSE_EVENT,
} from 'feature/progressIndicator/progressIndicatorSlice';
import {
	addEventSubscription,
	removeEventSubscription,
} from 'feature/sseEventManager/sseEventSlice';
import useDialogConfirm from 'hooks/DialogConfirm.hook';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppDispatch, RootState } from 'store/store';
import {
	Box,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	Typography,
} from 'styles';
import IconButtonTable from 'styles/button/IconButtonTable.styles';
import TableRowContent from 'styles/table/TableRowContent.styles';
import TableRowHeader from 'styles/table/TableRowHeader.styles';
import palette from 'styles/theme/Palette';
import { ECarriersList } from 'types/Carrier';
import { MISSING_EMAIL, SEARCH_MEMBER_ERROR } from 'types/dialogtypes';
import { SECTIONS } from 'types/SectionsVisibility';
import { SSE_EVENT_TYPE } from 'utils/sse.hook';
import { useTranslation } from 'utils/translations';

dayjs.extend(isBetween);

const ListMembers = ({
	selectedCarrier,
}: {
	selectedCarrier: ECarriersList;
}) => {
	const { members } = useSelector((store: RootState) => store.members);
	const { dateOfService } = useSelector((store: RootState) => store.form);
	const { t: tClientLabels } = useTranslation('', {
		keyPrefix: 'clientLabels',
	});
	const { t: tDbLabels } = useTranslation('', {
		keyPrefix: 'dbLabels',
	});
	const navigate = useNavigate();
	const dispatch = useDispatch<AppDispatch>();
	const dialogConfirm = useDialogConfirm();

	const getMemberDetail = (member: IMembers, subscriptionUuid: string) => {
		if (member.errors?.length) {
			dialogConfirm.open(
				SEARCH_MEMBER_ERROR,
				`${tDbLabels(`dialog.confirm.error.searchMember.${member.errors[0]}`)}`,
			);
			return;
		}
		navigate(
			`/check-and-submit/${subscriptionUuid}?dateOfService=${formatZuluForBE(
				dateOfService,
			)}`,
		);
	};

	/*************  SSE  ********** */
	const [memberSelected, setMemberSelected] = useState<IMembers | undefined>(
		undefined,
	);

	const { events } = useSelector((store: RootState) => store.sseEvents);

	useEffect(() => {
		if (memberSelected) {
			dispatch(addBusyIndicator({ progressIndicator: SSE_EVENT }));
		}
		setTimeout(() => {
			dispatch(removeBusyIndicator({ progressIndicator: SSE_EVENT })); // to avoid getting blocked for ever
		}, 10000);
	}, [dispatch, memberSelected]);

	useEffect(() => {
		dispatch(addEventSubscription(SSE_EVENT_TYPE.MEMBER));
		return () => {
			dispatch(removeEventSubscription(SSE_EVENT_TYPE.MEMBER));
		};
	}, [dispatch]);

	useEffect(() => {
		if (!memberSelected) return;
		const subscriptionUuid = events.find(
			ev => memberSelected.fiscalCode === ev.key,
		)?.value;
		if (subscriptionUuid) {
			// 07/11/2025 : unable to test, probably not needed and navigate() is enough
			dispatch(
				getMemberDetails({
					subscriptionUuid,
					dateOfService: formatDayjsForBE(dateOfService),
					callback: () => {
						navigate(
							`/check-and-submit/${subscriptionUuid}?dateOfService=${formatZuluForBE(
								dateOfService,
							)}`,
						);
					},
				}),
			);
			dispatch(removeBusyIndicator({ progressIndicator: SSE_EVENT }));
			setMemberSelected(undefined);
		}
	}, [dateOfService, dispatch, events, memberSelected, navigate]);
	/*************************** */

	return members?.length ? (
		<Box>
			<Typography variant="title5" sx={{ mb: { sm: 1.875, md: 2.5 } }}>
				{tClientLabels('check-and-submit.membersListLabel.results')}
			</Typography>
			<TableContainer sx={{ width: '820px', maxWidth: '820px' }}>
				<Table sx={{ borderCollapse: 'separate', borderSpacing: '0 5px' }}>
					<TableHead>
						<TableRowHeader>
							<TableCell>
								<Typography variant="bodyLink">
									{tClientLabels('check-and-submit.membersListLabel.memberName')}
								</Typography>
							</TableCell>
							<TableCell>
								<SectionVisible
									carrier={{ customLayout: selectedCarrier }}
									sectionName={SECTIONS.FISCAL_CODE}
								>
									<Typography variant="bodyLink">
										{tClientLabels('check-and-submit.membersListLabel.fiscalCode')}
									</Typography>
								</SectionVisible>
								<SectionVisible
									carrier={{ customLayout: selectedCarrier }}
									sectionName={SECTIONS.PPS_CODE}
								>
									<Typography variant="bodyLink">
										{tClientLabels('check-and-submit.membersListLabel.PpsCode')}
									</Typography>
								</SectionVisible>
							</TableCell>
							<TableCell>
								<Typography variant="bodyLink">
									{tClientLabels('check-and-submit.membersListLabel.relationship')}
								</Typography>
							</TableCell>
							<TableCell>
								<Typography variant="bodyLink">
									{tClientLabels('check-and-submit.membersListLabel.dateOfBirth')}
								</Typography>
							</TableCell>
						</TableRowHeader>
					</TableHead>
					<TableBody>
						{!!members?.length &&
							members?.map(member => {
								const {
									_id,
									firstName,
									lastName,
									fiscalCodeMasked,
									subscriptions,
									birthDate,
								} = member;
								return subscriptions.map((subs: IMemberSubscriptions) => {
									const { relationship, subscriptionUuid, missingInfo } = subs;
									return (
										<TableRowContent
											key={`${_id}_${subscriptionUuid}`}
											onClick={() => {
												if (missingInfo) {
													dialogConfirm.open(MISSING_EMAIL);
													return;
												}
												if (subscriptionUuid || member.errors?.length) {
													getMemberDetail(member, subscriptionUuid);
												} else {
													setMemberSelected(member);
												}
											}}
											sx={{
												'& > td:last-of-type': {
													padding: '0!important',
													position: 'sticky',
													right: 0,
													top: 0,
												},
											}}
										>
											<TableCell>
												<Typography variant="body">
													{firstName} {lastName}
												</Typography>
											</TableCell>
											<TableCell>
												<Typography variant="body">{fiscalCodeMasked}</Typography>
											</TableCell>
											<TableCell>
												{relationship && (
													<Typography variant="body">
														{tClientLabels(
															`check-and-submit.membersListLabel.${relationship}`,
														)}
													</Typography>
												)}
											</TableCell>
											<TableCell>
												<Typography variant="body">{getShortDate(birthDate)}</Typography>
											</TableCell>
											<TableCell>
												<Stack
													sx={{
														position: 'sticky',
														right: '0',
														zIndex: '120',
														alignItems: 'center',
														justifyContent: 'end',
														backgroundColor: palette.white.main,
														gap: '10px',
													}}
													direction="row"
												>
													{missingInfo ? (
														<ErrorInfoIcon
															aria-label={`${tClientLabels('dashboard.buttons.noEmail')}`}
														/>
													) : null}
													<IconButtonTable
														sx={{
															left: 0,
															width: 32,
															height: 32,
															pr: '9px',
														}}
														aria-label={`${tClientLabels('dashboard.buttons.viewClaims')}`}
														disabled={missingInfo}
													>
														{missingInfo ? (
															<DisabledChevronRightIcon />
														) : (
															<ChevronRightIcon />
														)}
													</IconButtonTable>
												</Stack>
											</TableCell>
										</TableRowContent>
									);
								});
							})}
					</TableBody>
				</Table>
			</TableContainer>
		</Box>
	) : (
		<></>
	);
};

export default ListMembers;
